import { Inject, Injectable } from '@angular/core';
import { CollectionViewer, SelectionChange } from '@angular/cdk/collections';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { FlatTreeControl } from '@angular/cdk/tree';
import { TreeBaseNode } from '@alfresco/adf-content-services';

import { WkTreeViewService } from '../../../../services/wk-tree-view.service';
import { NodeEntry, ResultSetPaging } from '@alfresco/js-api';
import { CommonConstants } from '../../../../shared/common.constant';

@Injectable()
export class TreeViewDataSource {
  treeNodes: TreeBaseNode[];
  dataChange = new BehaviorSubject<TreeBaseNode[]>([]);
  childrenSubscription = null;
  changeSubscription = null;
  onDestroy$: Subject<boolean> = new Subject<boolean>();

  get data(): TreeBaseNode[] {
    return this.treeNodes;
  }

  set data(value: TreeBaseNode[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(
    private readonly treeControl: FlatTreeControl<TreeBaseNode>,
    private readonly treeViewService: WkTreeViewService,
    @Inject(String) private readonly specificType: string,
    @Inject(String) private readonly forbiddenType: string
  ) {
    this.dataChange.subscribe((treeNodes) => (this.treeNodes = treeNodes));
  }

  connect(collectionViewer: CollectionViewer): Observable<TreeBaseNode[]> {
    this.changeSubscription = this.treeControl.expansionModel.changed.subscribe(
      (change) => {
        if (change.added && change.added.length > 0) {
          this.expandTreeNodes(change);
        } else if (change.removed) {
          this.reduceTreeNodes(change);
        }
      }
    );
    return merge(collectionViewer.viewChange, this.dataChange).pipe(
      map(() => this.data)
    );
  }

  disconnect() {
    if (this.childrenSubscription) {
      this.childrenSubscription.unsubscribe();
    }
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
    }
  }

  private expandTreeNodes(change: SelectionChange<TreeBaseNode>) {
    change.added.forEach((node) => this.expandNode(node));
  }

  private reduceTreeNodes(change: SelectionChange<TreeBaseNode>) {
    change.removed
      .slice()
      .reverse()
      .forEach((node) => this.toggleNode(node));
  }

  private expandNode(node: TreeBaseNode) {
    if (this.specificType === CommonConstants.applicationText) {
      this.treeViewService
        .getNodeChildren(node.nodeId)
        .pipe(
          map((data: ResultSetPaging) =>
            data.list.entries.map(
              (nodeEntry) => new TreeBaseNode(nodeEntry as NodeEntry)
            )
          ),
          takeUntil(this.onDestroy$)
        )
        .subscribe((treeNode: TreeBaseNode[]) => {
          this.getTreeNode(treeNode, node);
        });
    } else {
      this.childrenSubscription = this.treeViewService
        .getCustomTreeNodes(node.nodeId, this.specificType, this.forbiddenType)
        .subscribe((treeNode: TreeBaseNode[]) => {
          this.getTreeNode(treeNode, node);
        });
    }
  }

  getTreeNode(treeNode: TreeBaseNode[], node: TreeBaseNode) {
    const index = this.data.indexOf(node);
    if (!treeNode || index < 0) {
      node.expandable = false;
      return;
    }
    const nodes = treeNode.map((actualNode) => {
      actualNode.level = node.level + 1;
      return actualNode;
    });
    this.data.splice(index + 1, 0, ...nodes);
    this.dataChange.next(this.data);
  }

  toggleNode(node: TreeBaseNode) {
    const index = this.data.indexOf(node);
    let count = 0;
    for (
      let i = index + 1;
      i < this.data.length && this.data[i].level > node.level;
      i++, count++
    ) {
      /** empty block */
    }
    this.data.splice(index + 1, count);
    this.dataChange.next(this.data);
  }
}
